// ./src/pages/private/UsersPage/UsersPage.tsx
import React, { useState, useCallback } from "react";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";
import { useNavigate } from "react-router-dom";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import { UsersTable, UserFormModal } from "@/components/features";
import { useUsers } from "@/hooks";
import { User } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const UsersPage: React.FC<PageProps> = (props: PageProps) => {
  const { isAuthenticated } = useApp();
  const navigate = useNavigate();

  // Use the users hook for all data management
  const {
    // Data state
    users,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    sortBy,

    // Operations state
    isUpdating,
    removingUserId,
    selectedUser,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setFilters,
    setSortBy,

    // CRUD operations
    update,
    remove,

    // UI state management
    setSelectedUser,

    // Error handling
    lastError,
    clearError,
  } = useUsers({
    autoLoad: true,
    isAuthenticated,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      // Apply search filter
      const newFilters: any = {};
      if (value.trim()) {
        newFilters.keyword = value.trim();
        newFilters.searchIn = ["firstName", "lastName", "email", "username"];
      }

      // Update filters and reload data
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [sortBy, setFilters, list]
  );

  // Handle invite user button click - navigate to invitations page
  const handleInviteUser = useCallback(() => {
    navigate("/account/users/invitations");
  }, [navigate]);

  // Handle edit user
  const handleEditUser = useCallback(
    (user: User) => {
      setSelectedUser(user);
      setFormModalOpened(true);
    },
    [setSelectedUser]
  );

  // Handle remove user button click
  const handleRemoveUserClick = useCallback((user: User) => {
    setUserToDelete(user);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (userToDelete?.id) {
      const success = await remove(userToDelete.id);
      if (success) {
        setUserToDelete(null);
      }
    }
  }, [userToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setUserToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedUser(null);
    clearError();
  }, [setSelectedUser, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle user update
  const handleUpdateUser = useCallback(
    async (userId: string, userData: any) => {
      const result = await update(userId, userData);
      if (result) {
        setFormModalOpened(false);
        setSelectedUser(null);
      }
      return result;
    },
    [update, setSelectedUser]
  );

  // Get user display name for delete confirmation
  const getUserDisplayName = (user: User | null): string => {
    if (!user) return "";
    const parts = [user.firstName, user.lastName].filter(Boolean);
    if (parts.length > 0) return parts.join(" ");
    return user.email || user.username || "this user";
  };

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="invite-user"
                  iconName="UserPlus"
                  onClick={handleInviteUser}
                  disabled={loading || isUpdating}
                >
                  Invite User
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={loading || isUpdating || !!removingUserId}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search users..."
                  value={searchValue}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Users table */}
        <UsersTable
          users={users}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditUser={handleEditUser}
          onRemoveUser={handleRemoveUserClick}
          removingUserId={removingUserId}
        />

        {/* Empty state when no users */}
        <EmptyState
          display={isEmpty && !loading}
          title="No users yet"
          description="Start by inviting your first user to the platform"
          actionIcon="UserPlus"
          actionLabel="Invite User"
          onAction={handleInviteUser}
        />
      </Stack>

      {/* User form modal for edit */}
      <UserFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        user={selectedUser}
        onUpdateUser={handleUpdateUser}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!userToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete "${getUserDisplayName(
          userToDelete
        )}"?`}
        details="This action cannot be undone. The user will be permanently removed from the system."
        confirmInProgress={removingUserId === userToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
